'use client'

import { ArrowUpRight } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'

import { TiltCard } from '@/components/molecules/tilt-card.client'
import { dayWord, type RentalItemView, type RentalTermView, rentalPriceLabel } from '@/lib/rental'
import { iconFor, toneFor } from '@/lib/rental-icons'
import { PRODUCT_STAGE, RADIUS, SHEET_INK, SHEET_INK_QUIET, SURFACE_SHEET } from '@/lib/surface'
import { BODY, CARD_TITLE, MICRO, STAT } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * One piece of equipment in the grid.
 *
 * The card carries the scan-and-compare set and nothing else: photo, name,
 * category, price, one differentiator. Everything with more to say — the long
 * copy, the spec sheet, the day-price ladder — lives on the item's own page,
 * which is what the card opens.
 *
 * ## One action, not two
 *
 * The card used to carry a "Wypożycz" button as well as the card-wide link, and
 * both resolved to the SAME address — the file admitted as much. So the row
 * ended in a control that looked like a commit, sat in the position a commit
 * sits in, and did what clicking anywhere else on the card already did. Reading
 * it cost a decision ("is this different from opening it?") that had no answer.
 *
 * What replaces it is an affordance rather than a second target: an arrow that
 * lifts on hover, `aria-hidden`, no tab stop, nothing to mistake for a
 * separate destination. The whole card is the link, and it is the only one.
 *
 * ## A grey sheet with a black window in it
 *
 * The card is an opaque graphite sheet with white type on it, and the
 * photograph sits in a black rectangle at its top. The two halves are one
 * decision: the picture area spent three iterations trying to give a black
 * camera body an edge from UNDERNEATH, because there was nothing else to
 * contrast it with, and what fixed it was contrast from AROUND it instead.
 *
 * The sheet was white for exactly that reason and is grey now — owner's call
 * both times. Grey keeps the relationship that worked (sheet above window)
 * without putting a white slab on a dark page. What it loses is the size of
 * the step, so the window carries a hairline as well; see the token block in
 * `globals.css` for why black was the wrong half of the choice.
 *
 * See `SURFACE_SHEET` for why this does not use the app's `bg-white
 * text-neutral-900` pair, and `PRODUCT_STAGE` for the three shapes the window
 * went through first.
 *
 * ## The proportions
 *
 * The stage is 16:10 and the body is tight because this is a grid you scan. At
 * 4:3 with roomier padding the card ran 538px on a 1440 desktop, which put one
 * row on a screen and left the opening screen with no complete card on it at
 * all. Nothing was removed to buy that back except the duplicate button and one
 * line of the blurb.
 *
 * The link is on the whole card, not on the heading. `TiltCard` freezes its
 * tilt over `a` and `button` because a moving hit target was eating clicks, so
 * the anchor carries `data-tilt-through` to keep the effect alive under it.
 *
 * ## The image
 *
 * A failed load is tracked per card rather than in the listing's state. It used
 * to be an array of slugs on the parent that `onError` appended to without
 * de-duplicating, so a photo that failed repeatedly grew the array forever. A
 * boolean that belongs to the card it describes cannot have that bug.
 */
export function RentalCard({
  item,
  href,
  quote,
}: {
  item: RentalItemView
  /** The item's own page — where the lengths and the calendar are. */
  href: string
  /** The catalogue's price for the chosen length, or `null` when none is set. */
  quote: RentalTermView | null
}) {
  const [failed, setFailed] = useState(false)
  const Icon = iconFor(item.categorySlug)
  const tone = toneFor(item.categorySlug)
  const priced = item.dailyRateNet !== null

  return (
    <TiltCard className="h-full" strength={6}>
      <article
        className={cn(
          'group relative flex h-full flex-col overflow-hidden transition-shadow duration-200',
          RADIUS.feature,
          SURFACE_SHEET,
          // The hover cannot be the usual "lighten the glass" — this surface is
          // already at the top of its range. A sheet lifts instead.
          'hover:shadow-[0_24px_60px_-28px_rgb(0_0_0/0.9)]',
        )}
      >
        {/* The card IS the link.
            It has to be a real anchor covering the whole card, not an onClick,
            and it cannot be the heading's `::after`: the layers below carry
            `translateZ`, and a transform makes an element a containing block,
            so an overlay painted from inside one can never reach past it. It
            stopped at the body and left the photograph — two thirds of the
            card — dead to the pointer.

            `z-10`, not `z-0`: the photo layer below carries a transform, and a
            transformed element paints alongside `z-index: 0` in DOM order — so
            at `z-0` this overlay sat UNDER the photograph and only the lower
            half of the card was clickable.

            The name travels as `aria-label` because the visible heading below
            is not itself the anchor. */}
        <Link
          href={href}
          data-tilt-through
          aria-label={item.name}
          className={cn(
            'absolute inset-0 z-10',
            RADIUS.feature,
            // Dark, not white: the ring lands on the sheet, and a white ring
            // on a white card is a focus state nobody can see.
            'focus-visible:ring-2 focus-visible:ring-[var(--flou-card-ink)] focus-visible:outline-none',
          )}
        />

        {/* A missing file falls back to a labelled frame rather than an
            <Image> at a dead path, which paints alt text across the card. */}
        <div
          // The hairline along the bottom is what the white sheet did with
          // value alone. Against a grey sheet the window is only fourteen
          // points darker, which is a boundary you infer rather than see, so
          // the edge is drawn instead of implied.
          className="tilt-layer relative aspect-[16/10] w-full overflow-hidden rounded-t-3xl border-b border-[var(--flou-card-line)]"
          style={{ ['--depth' as string]: '30px', background: PRODUCT_STAGE }}
        >
          {item.image && !failed ? (
            <Image
              src={item.image}
              alt={item.imageAlt ?? item.name}
              fill
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              onError={() => setFailed(true)}
              // The cut-outs are square and the stage is wider than it is tall,
              // so `contain` fits them to its HEIGHT: this padding is the
              // product's size on the card, not a margin around it. Any lower
              // and the taller items (the 24-70 with its hood) reach the edge.
              className="object-contain p-4 transition-transform duration-500 group-hover:scale-[1.04] sm:p-5"
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/55">
              <Icon size={24} aria-hidden="true" />
              <span className={cn(MICRO, 'px-4 text-center font-medium')}>{item.name}</span>
            </div>
          )}
          {/* The badge is the only thing on the card that says what KIND of
              object this is, and it was the same grey on all eleven. It carries
              the category's own hue now — on the chip, not on the stage, because
              anything saturated under a cut-out fights the photograph. */}
          <span
            className={cn(
              MICRO,
              'absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-semibold uppercase backdrop-blur-md',
            )}
            style={{
              color: tone,
              borderColor: `color-mix(in oklab, ${tone} 40%, transparent)`,
              backgroundColor: `color-mix(in oklab, ${tone} 14%, oklch(0.12 0.02 300 / 0.75))`,
            }}
          >
            <Icon size={11} aria-hidden="true" />
            {item.categoryLabel}
          </span>
        </div>

        {/* No `tilt-layer` here, deliberately: a transformed element is its own
            stacking context, and the parallax this layer used to carry trapped
            everything inside it below the card-wide link. `pointer-events-none`
            passes clicks through to that link — there is nothing in here that
            takes its own any more. */}
        <div className="pointer-events-none relative flex flex-1 flex-col gap-1.5 p-4">
          <h2 className={cn(CARD_TITLE, SHEET_INK, 'group-hover:underline')}>{item.name}</h2>
          {/* One line. The blurbs run 47–74 characters and the second line was
              buying a sentence the item's own page prints in full. Clamped
              rather than trimmed so a row of cards still ends level. */}
          <p className={cn(BODY, SHEET_INK_QUIET, 'line-clamp-1 flex-1')}>{item.blurb}</p>

          <div className="mt-1.5 flex items-end justify-between gap-3 border-t border-[var(--flou-card-line)] pt-3">
            <span className="min-w-0">
              {/* The number people actually compare. It was set at CARD_TITLE,
                  the same size as the product name directly above it, so the
                  row had two headlines and no price. */}
              <span className={cn(STAT, SHEET_INK, 'block')}>
                {quote && priced
                  ? `${Math.round(quote.priceNet)} zł`
                  : rentalPriceLabel(item.dailyRateNet, item.rateCount)}
              </span>
              <span className={cn(MICRO, SHEET_INK_QUIET, 'mt-0.5 block')}>
                {quote && priced ? (
                  <>
                    za {quote.days} {dayWord(quote.days)} netto
                    {item.dailyRateNet !== null &&
                    item.dailyRateNet * quote.days > quote.priceNet ? (
                      <span className={cn(SHEET_INK, 'font-semibold')}>
                        {' '}
                        · −
                        {Math.round((1 - quote.priceNet / (item.dailyRateNet * quote.days)) * 100)}%
                      </span>
                    ) : null}
                  </>
                ) : priced ? (
                  'netto za dobę'
                ) : (
                  'wycenimy po zapytaniu'
                )}
              </span>
            </span>

            {/* Affordance, not a target: the card is already the link, and a
                second focusable control pointing at the same address is what
                this row used to get wrong. */}
            <span
              aria-hidden="true"
              className={cn(
                SHEET_INK_QUIET,
                'mb-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-full border border-[var(--flou-card-line)] transition-colors duration-200',
                'group-hover:border-[var(--flou-card-ink)] group-hover:bg-[var(--flou-card-ink)] group-hover:text-[var(--flou-card-sheet)]',
              )}
            >
              <ArrowUpRight size={15} strokeWidth={2.2} />
            </span>
          </div>
        </div>
      </article>
    </TiltCard>
  )
}
